import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls =
  "w-full border border-line bg-transparent px-3 py-2.5 font-mono text-xs text-bone placeholder:text-bone/30 outline-none transition-colors focus:border-violet";

const lines = (arr) => (arr || []).join("\n");
const toLines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

function ProductEditor({ product, token, onSaved }) {
  const [d, setD] = useState({
    ...product,
    price: product.price ?? "",
    details: lines(product.details),
    care: lines(product.care),
    images: lines(product.images),
    sizes: product.sizes.length ? product.sizes : [{ label: "", stock: 0 }],
    variants: product.variants.length ? product.variants : [{ label: "", stock: 0 }],
    size_chart: product.size_chart || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(
        `/admin/products/${d.id}`,
        {
          name: d.name,
          colour: d.colour,
          category: d.category,
          collection: d.collection || "",
          price: d.price === "" ? null : Number(d.price),
          description: d.description,
          details: toLines(d.details),
          care: toLines(d.care),
          fit_note: d.fit_note,
          images: toLines(d.images),
          sizes: d.sizes.filter((s) => s.label.trim()).map((s) => ({ label: s.label.trim(), stock: Number(s.stock) || 0 })),
          variants: d.variants.filter((v) => v.label.trim()).map((v) => ({ label: v.label.trim(), stock: Number(v.stock) || 0 })),
          size_chart: d.size_chart,
          featured: !!d.featured,
          position: Number(d.position) || 0,
        },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`${d.name} SAVED`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SAVE FAILED");
    }
    setSaving(false);
  };

  return (
    <div className="border border-line p-5" data-testid={`admin-editor-${product.slug}`}>
      <div className="flex items-center justify-between gap-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{product.slug}</p>
        <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">
          <input type="checkbox" checked={!!d.featured} onChange={(e) => setD({ ...d, featured: e.target.checked })} />
          FEATURED
        </label>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <input data-testid={`admin-name-${product.slug}`} value={d.name} onChange={set("name")} placeholder="NAME" className={inputCls} />
        <input value={d.colour} onChange={set("colour")} placeholder="COLOUR" className={inputCls} />
        <input value={d.category} onChange={set("category")} placeholder="CATEGORY" className={inputCls} />
        <input value={d.collection || ""} onChange={set("collection")} placeholder="COLLECTION (jojo-drop-001 / evangelion-drop)" className={inputCls} />
        <input value={d.price} onChange={set("price")} type="number" placeholder="PRICE (blank = TBA)" className={inputCls} />
        <input value={d.fit_note} onChange={set("fit_note")} placeholder="FIT NOTE" className={`${inputCls} sm:col-span-2`} />
        <textarea value={d.description} onChange={set("description")} placeholder="DESCRIPTION" rows={3} className={`${inputCls} sm:col-span-2`} />
        <textarea value={d.images} onChange={set("images")} placeholder="IMAGE URLS — ONE PER LINE" rows={3} className={`${inputCls} sm:col-span-2`} />
        <textarea value={d.details} onChange={set("details")} placeholder="DETAILS — ONE PER LINE" rows={3} className={inputCls} />
        <textarea value={d.care} onChange={set("care")} placeholder="CARE — ONE PER LINE" rows={3} className={inputCls} />
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">SIZES (LABEL : STOCK)</p>
          {d.sizes.map((s, i) => (
            <div key={i} className="mt-2 flex gap-2">
              <input
                value={s.label}
                onChange={(e) => setD({ ...d, sizes: d.sizes.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })}
                placeholder="S"
                className={`${inputCls} w-24`}
              />
              <input
                value={s.stock}
                type="number"
                onChange={(e) => setD({ ...d, sizes: d.sizes.map((x, j) => (j === i ? { ...x, stock: e.target.value } : x)) })}
                placeholder="0"
                className={`${inputCls} flex-1`}
              />
              <button
                onClick={() => setD({ ...d, sizes: d.sizes.filter((_, j) => j !== i) })}
                className="border border-line px-3 font-mono text-[10px] text-bone/50 hover:text-magenta"
              >
                X
              </button>
            </div>
          ))}
          <button
            onClick={() => setD({ ...d, sizes: [...d.sizes, { label: "", stock: 5 }] })}
            className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:text-bone"
          >
            + ADD SIZE
          </button>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">VARIANTS (LABEL : STOCK)</p>
          {d.variants.map((v, i) => (
            <div key={i} className="mt-2 flex gap-2">
              <input
                value={v.label}
                onChange={(e) => setD({ ...d, variants: d.variants.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })}
                placeholder="GOLD-TONE BUCKLE"
                className={`${inputCls} flex-1`}
              />
              <input
                value={v.stock}
                type="number"
                onChange={(e) => setD({ ...d, variants: d.variants.map((x, j) => (j === i ? { ...x, stock: e.target.value } : x)) })}
                placeholder="0"
                className={`${inputCls} w-24`}
              />
              <button
                onClick={() => setD({ ...d, variants: d.variants.filter((_, j) => j !== i) })}
                className="border border-line px-3 font-mono text-[10px] text-bone/50 hover:text-magenta"
              >
                X
              </button>
            </div>
          ))}
          <button
            onClick={() => setD({ ...d, variants: [...d.variants, { label: "", stock: 5 }] })}
            className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold hover:text-bone"
          >
            + ADD VARIANT
          </button>
        </div>
      </div>
      <button
        data-testid={`admin-save-${product.slug}`}
        onClick={save}
        disabled={saving}
        className="mt-5 bg-violet px-8 py-3 font-display text-lg tracking-[0.15em] text-bone transition-colors hover:bg-magenta disabled:opacity-50"
      >
        {saving ? "SAVING…" : "SAVE PRODUCT"}
      </button>
    </div>
  );
}

const SHIPPING_STAGES = {
  1: "PREPARING — goods bought internationally, being readied to ship",
  2: "IN AIR — flying to India now",
  3: "LANDED — arrived in India, a few days from their door",
};

function OrdersView({ token }) {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/orders", { headers: { "X-Admin-Token": token } });
      setOrders(r.data);
    } catch (e) {
      setOrders([]);
      toast.error(e.response?.data?.detail || "FAILED TO LOAD ORDERS");
    }
  };

  const setStatus = async (o, status) => {
    setBusy(o.number);
    try {
      const r = await api.put(
        `/admin/orders/${o.id}/status`,
        { status },
        { headers: { "X-Admin-Token": token } }
      );
      setOrders((prev) => prev.map((x) => (x.id === o.id ? r.data : x)));
      toast.success(`${o.number} marked ${status.toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "STATUS UPDATE FAILED");
    } finally {
      setBusy(null);
    }
  };

  const cancelOrder = async (o) => {
    if (!window.confirm(`Cancel unpaid order ${o.number} and release its stock?`)) return;
    setBusy(o.number);
    try {
      const r = await api.post(
        `/admin/orders/${o.id}/cancel`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      setOrders((prev) => prev.map((x) => (x.id === o.id ? r.data : x)));
      toast.success(`${o.number} cancelled — stock released`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "CANCEL FAILED");
    } finally {
      setBusy(null);
    }
  };

  const markPaid = async (o) => {
    if (!window.confirm(`Mark ${o.number} as PAID?\n\nOnly do this if the payment shows CAPTURED in your Razorpay dashboard. This emails the customer their confirmation.`)) return;
    setBusy(o.number);
    try {
      const r = await api.post(
        `/admin/orders/${o.id}/mark-paid`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      setOrders((prev) => prev.map((x) => (x.id === o.id ? r.data : x)));
      toast.success(`${o.number} marked paid — confirmation emailed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "MARK PAID FAILED");
    } finally {
      setBusy(null);
    }
  };

  const resendEmail = async (o) => {
    setBusy(o.number);
    try {
      const r = await api.post(
        `/admin/orders/${o.id}/resend-confirmation`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Confirmation re-sent to ${r.data.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "RESEND FAILED");
    } finally {
      setBusy(null);
    }
  };

  const sendShippingUpdate = async (o, stage) => {
    if (!window.confirm(`Send the "${SHIPPING_STAGES[stage]}" email to ${o.customer.email}?`)) return;
    setBusy(o.number);
    try {
      const r = await api.post(
        `/admin/orders/${o.id}/shipping-update`,
        { stage },
        { headers: { "X-Admin-Token": token } }
      );
      if (r.data?.sent) {
        toast.success(`Shipping update sent to ${o.customer.email}`);
        setOrders((prev) =>
          prev.map((x) =>
            x.id === o.id ? { ...x, shipping_stages_sent: r.data.stages_sent || [] } : x
          )
        );
      } else {
        toast.error("EMAIL FAILED TO SEND — PLEASE RETRY IN A MOMENT");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "EMAIL FAILED");
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusMeta = {
    created: { label: "AWAITING PAYMENT", cls: "text-gold" },
    cancelled: { label: "CANCELLED", cls: "text-magenta" },
    paid: { label: "PAID", cls: "text-violet" },
    shipped: { label: "SHIPPED", cls: "text-emerald-300" },
    delivered: { label: "DELIVERED", cls: "text-emerald-400" },
  };

  const [customOpen, setCustomOpen] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");

  if (orders === null) {
    return <p className="py-20 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-bone/40">LOADING…</p>;
  }

  const sendCustomEmail = async (o) => {
    if (!customBody.trim()) {
      toast.error("WRITE A MESSAGE FIRST");
      return;
    }
    setBusy(o.number);
    try {
      await api.post(
        `/admin/orders/${o.id}/email-custom`,
        { subject: customSubject, body: customBody },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Email sent to ${o.customer.email}`);
      setCustomOpen("");
      setCustomSubject("");
      setCustomBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "EMAIL FAILED");
    } finally {
      setBusy(null);
    }
  };

  const renderCard = (o, dim = false) => {
    const meta = statusMeta[o.status] || { label: o.status.toUpperCase(), cls: "text-bone/50" };
    return (
      <div key={o.id} className={`border p-5 ${dim ? "border-line/60 opacity-70" : "border-line"}`} data-testid={`admin-order-${o.number}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-xs tracking-[0.2em] text-gold">{o.number}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/50" data-testid={`admin-status-${o.number}`}>
                {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })} ·{" "}
                <span className={meta.cls}>{meta.label}</span> · {o.payment_mode}
              </p>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">ITEMS</p>
                {(o.items || []).map((it, i) => (
                  <p key={i} className="mt-1.5 font-mono text-xs text-bone/80">
                    {it.name}
                    {it.size ? ` — ${it.size}` : ""}
                    {it.variant ? ` · ${it.variant}` : ""} × {it.qty}
                    <span className="text-bone/40"> · ₹{(it.price * it.qty).toLocaleString("en-IN")}</span>
                  </p>
                ))}
                {(o.items || []).length === 0 && (
                  <p className="mt-1.5 font-mono text-xs text-bone/40">(details unavailable — restored placeholder)</p>
                )}
                <p className="mt-3 font-display text-xl tracking-[0.1em] text-bone">
                  TOTAL ₹{(o.total || 0).toLocaleString("en-IN")}
                </p>
              </div>
              <div className="font-mono text-xs leading-relaxed text-bone/70">
                <p className="text-[10px] uppercase tracking-[0.25em] text-bone/50">CUSTOMER</p>
                <p className="mt-1.5 text-bone">{o.customer.name}</p>
                <p>{o.customer.email}</p>
                <p>{o.customer.phone}</p>
                {o.customer.instagram && (
                  <p className="text-gold" data-testid={`admin-customer-instagram-${o.number}`}>
                    @{o.customer.instagram} — DM HERE FOR ISSUES
                  </p>
                )}
                <p className="mt-2 text-bone/60">
                  {o.customer.address}, {o.customer.city}, {o.customer.state} {o.customer.pincode}
                </p>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 border-t border-line pt-4">
              {o.status === "paid" && (
                <button
                  data-testid={`admin-status-ship-${o.number}`}
                  disabled={busy === o.number}
                  onClick={() => setStatus(o, "shipped")}
                  className="border border-gold/60 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink disabled:opacity-40"
                >
                  {busy === o.number ? "SAVING…" : "MARK SHIPPED"}
                </button>
              )}
              {o.status === "shipped" && (
                <button
                  data-testid={`admin-status-deliver-${o.number}`}
                  disabled={busy === o.number}
                  onClick={() => setStatus(o, "delivered")}
                  className="border border-emerald-400/60 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-300 transition-colors hover:bg-emerald-400 hover:text-ink disabled:opacity-40"
                >
                  {busy === o.number ? "SAVING…" : "MARK DELIVERED"}
                </button>
              )}
              {o.status === "delivered" && (
                <button
                  disabled
                  className="cursor-default border border-emerald-400/40 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400/80"
                >
                  ✓ DELIVERED
                </button>
              )}
              {o.status === "created" && (
                <button
                  data-testid={`admin-status-cancel-${o.number}`}
                  disabled={busy === o.number}
                  onClick={() => cancelOrder(o)}
                  className="border border-magenta/60 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-magenta transition-colors hover:bg-magenta hover:text-ink disabled:opacity-40"
                >
                  {busy === o.number ? "SAVING…" : "CANCEL & RELEASE STOCK"}
                </button>
              )}
              {(o.status === "failed" || o.status === "cancelled") && (
                <button
                  data-testid={`admin-mark-paid-${o.number}`}
                  disabled={busy === o.number}
                  onClick={() => markPaid(o)}
                  className="border border-gold/60 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink disabled:opacity-40"
                >
                  {busy === o.number ? "SAVING…" : "MARK AS PAID"}
                </button>
              )}
              {(o.status === "paid" || o.status === "shipped" || o.status === "delivered" || o.status === "failed" || o.status === "cancelled") && (
                <button
                  data-testid={`admin-resend-email-${o.number}`}
                  disabled={busy === o.number}
                  onClick={() => resendEmail(o)}
                  className="border border-line px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50 transition-colors hover:text-bone disabled:opacity-40"
                >
                  {busy === o.number ? "SENDING…" : "RESEND EMAIL"}
                </button>
              )}
              <button
                data-testid={`admin-custom-email-${o.number}`}
                onClick={() => setCustomOpen(customOpen === o.number ? "" : o.number)}
                className="border border-gold/50 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink"
              >
                {customOpen === o.number ? "CLOSE" : "SEND CUSTOM EMAIL"}
              </button>
              {customOpen === o.number && (
                <div className="space-y-3 border border-gold/40 bg-ink/60 p-4">
                  <input
                    data-testid={`custom-email-subject-${o.number}`}
                    value={customSubject}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    placeholder="SUBJECT (OPTIONAL)"
                    className="w-full border border-line bg-transparent px-3 py-2 font-mono text-[11px] text-bone placeholder:text-bone/30"
                  />
                  <textarea
                    data-testid={`custom-email-body-${o.number}`}
                    value={customBody}
                    onChange={(e) => setCustomBody(e.target.value)}
                    rows={4}
                    placeholder="Write your message to the customer…"
                    className="w-full border border-line bg-transparent px-3 py-2 text-sm text-bone placeholder:text-bone/30"
                  />
                  <button
                    data-testid={`custom-email-send-${o.number}`}
                    disabled={busy === o.number}
                    onClick={() => sendCustomEmail(o)}
                    className="bg-gold px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ink transition-opacity hover:opacity-80 disabled:opacity-40"
                  >
                    {busy === o.number ? "SENDING…" : "SEND"}
                  </button>
                </div>
              )}
              {["paid", "shipped", "delivered"].includes(o.status) && (
                <div className="w-full border-t border-line/60 pt-3" data-testid={`admin-shipping-updates-${o.number}`}>
                  <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.25em] text-bone/40">
                    SHIPPING UPDATES — SEND AS THE JOURNEY HAPPENS
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3].map((stage) => {
                      const sent = (o.shipping_stages_sent || []).includes(stage);
                      return (
                        <button
                          key={stage}
                          data-testid={`admin-ship-stage-${stage}-${o.number}`}
                          disabled={busy === o.number || sent}
                          onClick={() => sendShippingUpdate(o, stage)}
                          className={
                            sent
                              ? "cursor-default border border-line/50 bg-transparent px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/30"
                              : "border border-violet/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-violet transition-colors hover:bg-violet hover:text-bone disabled:opacity-40"
                          }
                        >
                          {sent
                            ? ["", "1 · PREPARING — SENT ✓", "2 · IN AIR — SENT ✓", "3 · LANDED — SENT ✓"][stage]
                            : busy === o.number
                            ? "SENDING…"
                            : ["", "1 · PREPARING", "2 · IN AIR", "3 · LANDED"][stage]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {(o.status === "shipped" || o.status === "delivered") && (
                <button
                  onClick={() => setStatus(o, o.status === "delivered" ? "shipped" : "paid")}
                  className="border border-line px-5 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50 transition-colors hover:text-bone"
                >
                  UNDO
                </button>
              )}
            </div>
          </div>
        );
  };

  const groups = {
    unpaid: orders.filter((o) => o.status === "created"),
    paid: orders.filter((o) => o.status === "paid"),
    shipped: orders.filter((o) => o.status === "shipped"),
    delivered: orders.filter((o) => o.status === "delivered"),
    closed: orders.filter((o) => o.status === "failed" || o.status === "cancelled"),
  };

  const section = (title, list, dim = false) => (
    <div>
      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/50">
        {title} — {list.length}
      </p>
      <div className="space-y-5">
        {list.length === 0 && (
          <p className="border border-line/50 p-4 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/30">
            NONE
          </p>
        )}
        {list.map((o) => renderCard(o, dim))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="admin-orders">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">
        {orders.length} ORDER{orders.length === 1 ? "" : "S"} · GROUPED BY STATUS
      </p>
      <div className="grid items-start gap-8 lg:grid-cols-2">
        {section("UNPAID — AWAITING PAYMENT", groups.unpaid)}
        {section("PAID — READY TO SHIP", groups.paid)}
      </div>
      <div className="grid items-start gap-8 lg:grid-cols-2">
        {section("SHIPPED", groups.shipped)}
        {section("DELIVERED", groups.delivered)}
      </div>
      {section("CLOSED — FAILED / CANCELLED", groups.closed, true)}
      <button
        onClick={load}
        className="border border-line px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/60 hover:border-gold hover:text-gold"
      >
        REFRESH ORDERS
      </button>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState("");
  const [products, setProducts] = useState(null);
  const [tab, setTab] = useState("products");

  useEffect(() => {
    document.title = "ADMIN — REQUIEM FEVER";
  }, []);

  const load = async (t) => {
    try {
      const r = await api.get("/admin/products", { headers: { "X-Admin-Token": t } });
      setProducts(r.data);
    } catch (e) {
      setProducts([]);
      toast.error(e.response?.data?.detail || "ACCESS DENIED");
    }
  };

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-page">
      <div className="mx-auto max-w-4xl px-5 py-14 sm:px-8">
        <h1 className="font-display text-5xl tracking-[0.01em] text-bone">CATALOGUE ADMIN</h1>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50">
          EDIT NAMES, PRICES, DESCRIPTIONS, IMAGES, SIZES & STOCK
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <input
            data-testid="admin-token-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="ADMIN TOKEN"
            className={`${inputCls} flex-1`}
          />
          <button
            data-testid="admin-unlock-button"
            onClick={() => token && load(token.trim())}
            className="bg-gold px-8 py-2.5 font-display text-lg tracking-[0.15em] text-ink transition-colors hover:bg-bone"
          >
            UNLOCK
          </button>
        </div>
        {products && (
          <>
            <div className="mt-10 flex gap-2" data-testid="admin-tabs">
              <button
                data-testid="admin-tab-products"
                onClick={() => setTab("products")}
                className={`px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                  tab === "products" ? "bg-bone text-ink" : "border border-line text-bone/60 hover:text-bone"
                }`}
              >
                PRODUCTS
              </button>
              <button
                data-testid="admin-tab-orders"
                onClick={() => setTab("orders")}
                className={`px-6 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                  tab === "orders" ? "bg-bone text-ink" : "border border-line text-bone/60 hover:text-bone"
                }`}
              >
                ORDERS
              </button>
            </div>
            {tab === "products" ? (
              <div className="mt-8 space-y-6">
                {products.map((p) => (
                  <ProductEditor key={p.id} product={p} token={token.trim()} onSaved={() => load(token.trim())} />
                ))}
              </div>
            ) : (
              <div className="mt-8">
                <OrdersView token={token.trim()} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
